import { createFileRoute, Link } from '@tanstack/react-router';
import { lazy, Suspense, type ReactNode } from 'react';
import { JobsList } from '~/components/jobs-list/JobsList';

// Active MIT jobs — the old Operations "Active Jobs" tab, now the Operations
// Job Board view of the Jobs section (Operations itself dissolved).
const OperationsActiveJobs = lazy(() =>
  import('~/components/dashboards/operations/OperationsActiveJobs')
    .then((m) => ({ default: m.OperationsActiveJobs })));

/** The two views of the Jobs section, selected with `?view=`. */
type JobsView = 'jobs' | 'operations-board';

function BriefcaseIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="7" width="18" height="13" rx="2" /><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /><path d="M3 13h18" />
    </svg>
  );
}

function BoardIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18" /><path d="M15 3v18" />
    </svg>
  );
}

const VIEWS: { key: JobsView; label: string; icon: ReactNode }[] = [
  { key: 'jobs', label: 'Jobs', icon: <BriefcaseIcon /> },
  { key: 'operations-board', label: 'Operations Job Board', icon: <BoardIcon /> },
];

export const Route = createFileRoute('/_app/jobs/')({
  // `jobs` is the default, so it stays out of the URL entirely.
  validateSearch: (search: Record<string, unknown>): { view?: 'operations-board' } =>
    search.view === 'operations-board' ? { view: 'operations-board' } : {},
  component: JobsIndex,
});

function JobsIndex() {
  const { view } = Route.useSearch();
  const active: JobsView = view ?? 'jobs';
  return (
    <div className="flex w-full flex-col">
      <div className="border-b border-[#e2e8f0] bg-white px-4 sm:px-6">
        <nav className="-mb-px flex gap-1 overflow-x-auto">
          {VIEWS.map((v) => {
            const on = active === v.key;
            return (
              <Link
                key={v.key}
                to="/jobs"
                search={v.key === 'operations-board' ? { view: 'operations-board' as const } : {}}
                className="flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3.5 py-3 text-sm font-semibold transition-colors"
                style={on
                  ? { color: '#2563eb', borderColor: '#2563eb' }
                  : { color: '#64748b', borderColor: 'transparent' }}
                aria-current={on ? 'page' : undefined}
              >
                {v.icon}
                {v.label}
              </Link>
            );
          })}
        </nav>
      </div>
      {active === 'jobs' ? <JobsList /> : (
        <div className="w-full px-4 py-5 sm:px-6">
          <Suspense fallback={<div className="text-sm text-[var(--color-ink-muted)]">Loading…</div>}>
            <OperationsActiveJobs />
          </Suspense>
        </div>
      )}
    </div>
  );
}
